import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { EquipmentDetail } from '../../types'
import { Alert, Badge, Card, CardBody, CardHeader, EmptyState, Skeleton } from '../../components/ui'
import { AlertIcon, WrenchIcon } from '../../components/icons'

interface EquipmentContextResponse { context: string; equipment: EquipmentDetail }

export default function AdminEquipmentGuideDetail() {
  const { equipmentId } = useParams<{ equipmentId: string }>()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['equipment', 'detail', equipmentId],
    queryFn: () => api.get<EquipmentContextResponse>(`/equipment/${equipmentId}/context`),
  })

  const eq = data?.equipment

  if (isError) {
    return <Alert tone="danger">{(error as Error).message}</Alert>
  }
  if (isLoading || !eq) {
    return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>
  }

  const components = eq.components ?? []
  const problems = eq.problems ?? []
  const procedures = eq.procedures ?? []
  const pmSchedules = eq.pm_schedules ?? []
  const safety = eq.safety ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/equipment-guide" style={{ fontSize: 'var(--text-sm)' }}>← Equipment guide</Link>
          <h1 style={{ marginTop: 'var(--sp-2)' }}>{eq.equipment_name || eq.equipment_id}</h1>
          <p className="page-head__sub">
            {[eq.manufacturer, eq.model, titleCase(eq.equipment_class)].filter(Boolean).join(' · ') || 'Equipment reference'}
          </p>
        </div>
      </div>

      {eq.description && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardBody>{eq.description}</CardBody>
        </Card>
      )}

      <div className="grid grid--tiles-3" style={{ gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
        <Card><CardBody><div className="stat"><div className="stat__label">Components</div><div className="stat__value">{components.length}</div></div></CardBody></Card>
        <Card><CardBody><div className="stat"><div className="stat__label">Known problems</div><div className="stat__value">{problems.length}</div></div></CardBody></Card>
        <Card><CardBody><div className="stat"><div className="stat__label">Critical / shutdown</div><div className="stat__value">{eq.critical_problem_count ?? 0}</div></div></CardBody></Card>
      </div>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Components" sub={components.length ? `${components.length} on file` : undefined} />
        <CardBody flush>
          {components.length === 0 ? (
            <EmptyState icon={<WrenchIcon size={22} />} title="No components recorded" body="" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Component</th><th>Type</th><th>Service interval</th><th>Life</th><th /></tr></thead>
                <tbody>
                  {components.map((c) => (
                    <tr key={c.component_id}>
                      <td style={{ fontWeight: 600 }}>{c.component_name || c.component_id}</td>
                      <td>{titleCase(c.component_type) || '—'}</td>
                      <td>{c.service_interval_hours != null ? `${c.service_interval_hours} h` : '—'}</td>
                      <td>{c.typical_life_hours != null ? `${c.typical_life_hours} h` : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {c.is_safety_critical && <Badge tone="danger">Safety-critical</Badge>}
                        {c.is_wear_item && <Badge tone="warning">Wear item</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Known problems" sub={problems.length ? `${problems.length} on file` : undefined} />
        <CardBody flush>
          {problems.length === 0 ? (
            <EmptyState icon={<AlertIcon size={22} />} title="No problems recorded" body="" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Problem</th><th>Category</th><th>Component</th><th>Symptoms</th><th /></tr></thead>
                <tbody>
                  {problems.map((p) => (
                    <tr key={p.problem_id}>
                      <td style={{ fontWeight: 600 }}>{p.problem_title || p.problem_id}</td>
                      <td>{titleCase(p.problem_category) || '—'}</td>
                      <td>{p.primary_component_name || '—'}</td>
                      <td>{p.symptoms?.length ?? 0}</td>
                      <td style={{ textAlign: 'right' }}>
                        {p.safety_shutdown_required && <Badge tone="danger">Shutdown required</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Repair procedures" sub={procedures.length ? `${procedures.length} on file` : undefined} />
        <CardBody flush>
          {procedures.length === 0 ? (
            <EmptyState title="No procedures recorded" body="" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Procedure</th><th>Type</th><th className="table__num">Steps</th></tr></thead>
                <tbody>
                  {procedures.map((p) => (
                    <tr key={p.procedure_id}>
                      <td style={{ fontWeight: 600 }}>{p.title || p.procedure_id}</td>
                      <td>{titleCase(p.procedure_type) || '—'}</td>
                      <td className="table__num">{p.steps?.length ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardHeader title="Preventive maintenance" sub={pmSchedules.length ? `${pmSchedules.length} schedules` : undefined} />
        <CardBody flush>
          {pmSchedules.length === 0 ? (
            <EmptyState title="No PM schedules recorded" body="" />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Level</th><th className="table__num">Interval</th><th className="table__num">Crew</th><th className="table__num">Time</th></tr></thead>
                <tbody>
                  {pmSchedules.map((s) => (
                    <tr key={s.schedule_id}>
                      <td style={{ fontWeight: 600 }}>{s.pm_level || s.pm_id || s.schedule_id}</td>
                      <td className="table__num">{s.interval_hours != null ? `${s.interval_hours} h` : '—'}</td>
                      <td className="table__num">{s.crew_required ?? '—'}</td>
                      <td className="table__num">{s.total_time_hours != null ? `${s.total_time_hours} h` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {safety.length > 0 && (
        <Card>
          <CardHeader title="Safety" />
          <CardBody>
            <div className="stack" style={{ gap: 'var(--sp-4)' }}>
              {safety.map((s) => (
                <div key={s.safety_id}>
                  {!!s.hazards?.length && (
                    <p><strong>Hazards:</strong> {s.hazards.join(', ')}</p>
                  )}
                  {!!s.isolation_points?.length && (
                    <p><strong>Isolation points:</strong> {s.isolation_points.join(', ')}</p>
                  )}
                  {!!s.emergency_procedures?.length && (
                    <p><strong>Emergency procedures:</strong> {s.emergency_procedures.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </>
  )
}
